import {AbstractApiService} from "./AbstractApiService";
import {Menu, MenuItem} from "navigation-common/types/navigation-types";
import {API_SERVICE_URL} from "navigation-common/firebase-config";
import {AxiosResponse} from "axios";
import {securityService} from "navigation-common/SecurityService";

class NavigationService extends AbstractApiService {

    public async loadMenu(menuKey: string): Promise<Menu> {
        const response = await this.client.get(API_SERVICE_URL + "/menu/" + menuKey, this.buildJWTAuthAxiosConfig());
        return this.sanitizeMenu(response.data as Menu);
    }

    public async loadAllMenus(): Promise<Array<Menu>> {
        const response = await this.client.get(API_SERVICE_URL + "/allMenus", this.buildJWTAuthAxiosConfig());
        return this.sanitizeMenuArray(response.data as Menu[]);
    }

    public async loadMyMenu(spaceKey?: string): Promise<Menu> {
        const response = await this.client.get(API_SERVICE_URL + "/myMenu/" + spaceKey, this.buildJWTAuthAxiosConfig());
        return this.sanitizeMenu(response.data as Menu);
    }

    public async updateMenu(menu: Menu | null): Promise<AxiosResponse> {
        const cleanedUpMenu = menu ? this.cleanUpMenu(menu) : menu;
        const sanitizedMenu = cleanedUpMenu ? this.sanitizeMenu(cleanedUpMenu) : cleanedUpMenu;
        return this.client.patch(API_SERVICE_URL + "/updateMenu/" + menu?.key, sanitizedMenu, this.buildJWTAuthAxiosConfig());
    }

    public async changeDefaultMenu(menus: Array<Menu>, menu: Menu): Promise<void> {
        menu.default = true;

        for (const menuToChange of menus) {
            if (menuToChange.default && menuToChange.key !== menu.key) {
                menuToChange.default = false;
                await this.updateMenu(menuToChange);
            }
        }

        await this.updateMenu(menu);
    }

    public async createDemoMenu(): Promise<void> {
        await this.client.post(API_SERVICE_URL + "/createDemoMenu", {}, this.buildJWTAuthAxiosConfig());
    }

    public async createEmptyMenu(shouldBeDefault: boolean): Promise<void> {
        await this.client.post(API_SERVICE_URL + "/createEmptyMenu", {shouldBeDefault: shouldBeDefault}, this.buildJWTAuthAxiosConfig());
    }

    public async deleteMenu(menu: Menu | null): Promise<void> {
        if (menu) {
            await this.client.delete(API_SERVICE_URL + "/deleteMenu/" + menu.key, this.buildJWTAuthAxiosConfig());
        }
    }

    public async copyMenu(menu: Menu | null): Promise<Menu> {
        const sanitizedMenu = menu ? this.sanitizeMenu(menu) : menu;

        const response = await this.client.post(API_SERVICE_URL + "/duplicateMenu", sanitizedMenu, this.buildJWTAuthAxiosConfig());
        return this.sanitizeMenu(response.data as Menu);
    }

    public async isCurrentUserEditor(): Promise<boolean> {
        try {
            const response = await this.client.get(
                API_SERVICE_URL + "/isEditor",
                this.buildJWTAuthAxiosConfig()
            );
            return response.data as boolean;
        } catch (e) {
            console.warn("Check if current user is an editor failed: " + e);
        }
        return false;
    }

    private sanitizeMenu(menu: Menu): Menu {
        return {
            ...menu,
            name: menu.name ? securityService.sanitizeHtml(menu.name).trim() : menu.name,
            menuItems: this.sanitizeMenuItems(menu.menuItems)
        };
    }

    private cleanUpMenu(menu: Menu): Menu {
        return {
            ...menu,
            menuItems: this.cleanUpMenuItems(menu.menuItems)
        };
    }

    private cleanUpMenuItems(menuItems: MenuItem[]): MenuItem[] {
        if (menuItems) {
            return menuItems.map(menuItem => {
                return {
                    ...menuItem,
                    cqlString: menuItem.cqlEnabled ? menuItem.cqlString : undefined,
                    link: menuItem.cqlEnabled ? undefined : menuItem.link,
                    subMenuItems: menuItem.subMenuItems ? this.cleanUpMenuItems(menuItem.subMenuItems) : menuItem.subMenuItems,
                    imageUrl: !menuItem.imageStorageName ? menuItem.imageUrl : undefined,
                }
            });
        }

        return [];
    }

    private sanitizeMenuArray(menus: Menu[]): Menu[] {
        return menus.map(menu => {
            return this.sanitizeMenu(menu);
        });
    }

    private sanitizeMenuItems(menuItems: MenuItem[]): MenuItem[] {
        if (menuItems) {
            return menuItems.map(menuItem => {
                return {
                    ...menuItem,
                    name: menuItem.name ? securityService.sanitizeHtml(menuItem.name).trim() : menuItem.name,
                    link: menuItem.link ? securityService.sanitizeUrl(menuItem.link).trim() : menuItem.link,
                    imageUrl: menuItem.imageUrl ? securityService.sanitizeUrl(menuItem.imageUrl).trim() : menuItem.imageUrl,
                    subMenuItems: menuItem.subMenuItems ? this.sanitizeMenuItems(menuItem.subMenuItems) : menuItem.subMenuItems
                }
            });
        }

        return [];
    }

    public removeImageStorageNameFromMenu(menu: Menu): Menu {
        return {
            ...menu,
            menuItems: this.removeImageStorageNameFromMenuItems(menu.menuItems)
        };
    }

    private removeImageStorageNameFromMenuItems(menuItems: MenuItem[]): MenuItem[] {
        if (menuItems) {
            return menuItems.map(menuItem => {
                return {
                    ...menuItem,
                    imageStorageName: undefined,
                    imageUrl: menuItem.imageStorageName ? undefined : menuItem.imageUrl,
                    subMenuItems: menuItem.subMenuItems ? this.removeImageStorageNameFromMenuItems(menuItem.subMenuItems) : menuItem.subMenuItems
                }
            });
        }

        return [];
    }
}

export const navigationService = new NavigationService();
